import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { CheckCircle2, Download, Loader2, Clock } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Card, CardContent } from '../components/ui/card';
import { EsignTracker } from '../components/esign/EsignTracker';
import { esignAPI, saveBlob } from '../lib/esignAPI';
import { EsignHolds } from '../components/esign/EsignHolds';

// Never trusts the redirect: polls OUR backend, which re-fetches the envelope from the provider.
export default function SigningComplete() {
  const id = new URLSearchParams(window.location.search).get('letter');
  const [s, setS] = useState(null);
  const [tries, setTries] = useState(0);
  useEffect(() => {
    if (!id) return undefined;
    let stop = false;
    const tick = async (n) => {
      try { const r = await esignAPI.status(id); setS(r); if (['signed', 'onboarding_completed', 'declined'].includes(r.esign_status) || r.my_status === 'SIGNED' && r.signers.some((x) => x.status !== 'SIGNED')) return; } catch { /* keep polling */ }
      if (!stop && n < 30) { setTries(n + 1); setTimeout(() => tick(n + 1), 2000); }
    };
    tick(0);
    return () => { stop = true; };
  }, [id]);
  const done = s && ['signed', 'onboarding_completed'].includes(s.esign_status);
  const mineDone = s?.my_status === 'SIGNED' && !done;
  return (
    <div className="mx-auto max-w-3xl space-y-6 p-6" data-testid="signing-complete-page">
      <EsignTracker status={s?.esign_status || 'awaiting_signature'} consented />
      <Card><CardContent className="space-y-4 p-8 text-center">
        {done ? <>
          <CheckCircle2 className="mx-auto h-10 w-10 text-emerald-600" />
          <h1 className="text-2xl font-semibold" data-testid="signing-verified">Your engagement letter is signed</h1>
          <p className="text-sm text-muted-foreground">We verified your signature with the signing service. A copy is saved in your Documents.</p>
          {s.has_signed_pdf && <Button onClick={async () => saveBlob(await esignAPI.signedBlob(id), `${s.ps_document_id}-signed.pdf`)} data-testid="signing-download"><Download className="mr-2 h-4 w-4" />Download signed copy</Button>}
          {s.esign_status === 'signed' && <div className="text-left"><EsignHolds holds={s.holds} testId="signing-holds" /></div>}
          {s.esign_status === 'onboarding_completed' && <p className="text-sm text-emerald-700" data-testid="signing-onboarding-complete">Onboarding completed.</p>}
        </> : mineDone ? <>
          <Clock className="mx-auto h-10 w-10 text-sky-600" />
          <h1 className="text-2xl font-semibold" data-testid="signing-partial">Thanks – your signature is recorded</h1>
          <p className="text-sm text-muted-foreground">We&apos;re waiting for the other signatories. We&apos;ll email you when everyone has signed.</p>
        </> : s?.esign_status === 'declined' ? (
          <h1 className="text-xl font-semibold" data-testid="signing-declined">You declined to sign. Your accountant has been notified.</h1>
        ) : tries >= 30 ? (
          <p className="text-sm" data-testid="signing-slow">We haven&apos;t received confirmation yet. You can safely leave this page – we&apos;ll notify you once it&apos;s confirmed.</p>
        ) : <>
          <Loader2 className="mx-auto h-8 w-8 animate-spin text-primary" />
          <h1 className="text-xl font-semibold" data-testid="signing-checking">Checking your signature…</h1>
        </>}
        <Link to="/onboarding" className="block text-sm text-primary underline">Back to onboarding</Link>
      </CardContent></Card>
    </div>
  );
}
